import type {
  BuilderParams,
  FontId,
  PresetTheme,
  SemanticOverrides,
} from "../../themes/builder-constants";
import type {ReactNode} from "react";

import {createContext, use, useEffect, useRef, useState} from "react";

import {
  DEFAULT_BUILDER_PARAMS,
  fontIds,
  fontMap,
  formRadiusOptions,
  radiusOptions,
} from "../../themes/builder-constants";
import {generateBuilderCSS} from "../../themes/builder-utils";
import {injectBuilderStyles, removeBuilderStyles} from "../../utils/css-injection";

/* -------------------------------------------------------------------------------------------------
 * Types
 * -----------------------------------------------------------------------------------------------*/

interface HistoryEntry {
  params: BuilderParams;
  semanticOverrides: SemanticOverrides | undefined;
}

interface HistoryState {
  entries: HistoryEntry[];
  index: number;
}

export interface ThemeBuilderContextValue {
  params: BuilderParams;
  semanticOverrides: SemanticOverrides | undefined;
  isOpen: boolean;
  isCodePanelOpen: boolean;
  lockedKeys: Array<keyof BuilderParams>;
  canUndo: boolean;
  canRedo: boolean;
  setParam: <K extends keyof BuilderParams>(key: K, value: BuilderParams[K]) => void;
  setParams: (partial: Partial<BuilderParams>) => void;
  resetParams: () => void;
  toggleOpen: () => void;
  toggleCodePanel: () => void;
  toggleLock: (key: keyof BuilderParams) => void;
  randomize: () => void;
  loadPreset: (preset: PresetTheme) => void;
  undo: () => void;
  redo: () => void;
}

/* -------------------------------------------------------------------------------------------------
 * Context
 * -----------------------------------------------------------------------------------------------*/

const ThemeBuilderContext = createContext<ThemeBuilderContextValue | null>(null);

/* -------------------------------------------------------------------------------------------------
 * Font CDN Injection Helper
 * Tracks already-injected fonts to avoid duplicate <link> tags.
 * -----------------------------------------------------------------------------------------------*/

const injectedFonts = new Set<string>();

function injectFontLink(fontId: FontId): void {
  if (injectedFonts.has(fontId)) return;
  const config = fontMap[fontId];

  if (!config) return;

  const link = document.createElement("link");

  link.rel = "stylesheet";
  link.href = config.cdnUrl;
  link.id = `@project/font-${fontId}`;
  document.head.appendChild(link);
  injectedFonts.add(fontId);
}

/* -------------------------------------------------------------------------------------------------
 * History helpers
 * -----------------------------------------------------------------------------------------------*/

const MAX_HISTORY = 100;

const INITIAL_HISTORY: HistoryState = {
  entries: [{params: DEFAULT_BUILDER_PARAMS, semanticOverrides: undefined}],
  index: 0,
};

/** Appends a new entry, truncates any redo-future, and enforces the size cap. */
function pushEntry(prev: HistoryState, entry: HistoryEntry): HistoryState {
  const truncated = prev.entries.slice(0, prev.index + 1);
  const newEntries = [...truncated, entry];

  if (newEntries.length > MAX_HISTORY) {
    return {entries: newEntries.slice(-MAX_HISTORY), index: MAX_HISTORY - 1};
  }

  return {entries: newEntries, index: newEntries.length - 1};
}

/* -------------------------------------------------------------------------------------------------
 * Provider
 * -----------------------------------------------------------------------------------------------*/

export function ThemeBuilderProvider({children}: {children: ReactNode}) {
  const [historyState, setHistoryState] = useState<HistoryState>(INITIAL_HISTORY);
  const [isOpen, setIsOpen] = useState(false);
  const [isCodePanelOpen, setIsCodePanelOpen] = useState(false);
  const [lockedKeys, setLockedKeys] = useState<Array<keyof BuilderParams>>([]);

  // Tracks the last param key mutated — used for coalescing consecutive slider drags.
  const lastChangedKeyRef = useRef<keyof BuilderParams | null>(null);

  const {params, semanticOverrides} = historyState.entries[historyState.index];
  const canUndo = historyState.index > 0;
  const canRedo = historyState.index < historyState.entries.length - 1;

  // Inject font on mount (default) and on fontFamily change
  const prevFontRef = useRef<FontId | null>(null);

  useEffect(() => {
    if (prevFontRef.current !== params.fontFamily) {
      prevFontRef.current = params.fontFamily;
      injectFontLink(params.fontFamily);
    }
  }, [params.fontFamily]);

  // Sync CSS: inject when open + params change, remove when closed
  useEffect(() => {
    if (isOpen) {
      const css = generateBuilderCSS(params, semanticOverrides);

      injectBuilderStyles(css);
    } else {
      removeBuilderStyles();
    }
  }, [isOpen, params, semanticOverrides]);

  // Cleanup on unmount
  useEffect(
    () => () => {
      removeBuilderStyles();
    },
    [],
  );

  // Close code panel when the theme builder is closed
  useEffect(() => {
    if (!isOpen) setIsCodePanelOpen(false);
  }, [isOpen]);

  /**
   * Updates a single param. Consecutive calls for the SAME key are coalesced into one
   * history entry to prevent flooding the stack during slider drags.
   */
  const setParam = <K extends keyof BuilderParams>(key: K, value: BuilderParams[K]) => {
    const isSameKey = lastChangedKeyRef.current === key;

    lastChangedKeyRef.current = key;

    setHistoryState((prev) => {
      const current = prev.entries[prev.index];
      const updatedEntry: HistoryEntry = {
        params: {...current.params, [key]: value},
        semanticOverrides: current.semanticOverrides,
      };

      if (isSameKey) {
        // Coalesce: replace the current history entry in-place
        const newEntries = [...prev.entries];

        newEntries[prev.index] = updatedEntry;

        return {entries: newEntries, index: prev.index};
      }

      return pushEntry(prev, updatedEntry);
    });
  };

  /**
   * Atomically updates multiple params in a single history entry.
   * Always pushes a new snapshot — use this for multi-key mutations (e.g. color picker).
   */
  const setParams = (partial: Partial<BuilderParams>) => {
    lastChangedKeyRef.current = null;
    setHistoryState((prev) => {
      const current = prev.entries[prev.index];

      return pushEntry(prev, {
        params: {...current.params, ...partial},
        semanticOverrides: current.semanticOverrides,
      });
    });
  };

  const resetParams = () => {
    lastChangedKeyRef.current = null;
    setHistoryState((prev) =>
      pushEntry(prev, {params: DEFAULT_BUILDER_PARAMS, semanticOverrides: undefined}),
    );
    setLockedKeys([]);
  };

  const toggleOpen = () => setIsOpen((prev) => !prev);

  const toggleCodePanel = () => setIsCodePanelOpen((prev) => !prev);

  const toggleLock = (key: keyof BuilderParams) => {
    setLockedKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const randomize = () => {
    lastChangedKeyRef.current = null;
    setHistoryState((prev) => {
      const current = prev.entries[prev.index];
      const next = {...current.params};
      const rand = (min: number, max: number) => Math.random() * (max - min) + min;
      const pick = <T,>(arr: readonly T[]) => arr[Math.floor(Math.random() * arr.length)] as T;

      if (!lockedKeys.includes("hue")) next.hue = Math.round(rand(0, 360) * 100) / 100;
      if (!lockedKeys.includes("chroma")) next.chroma = Math.round(rand(0.1, 0.26) * 10000) / 10000;
      if (!lockedKeys.includes("lightness"))
        next.lightness = Math.round(rand(0.5, 0.85) * 10000) / 10000;
      if (!lockedKeys.includes("base")) next.base = Math.round(rand(0, 0.02) * 10000) / 10000;
      if (!lockedKeys.includes("radius")) next.radius = pick(radiusOptions).id;
      if (!lockedKeys.includes("formRadius")) next.formRadius = pick(formRadiusOptions).id;
      if (!lockedKeys.includes("fontFamily")) next.fontFamily = pick(fontIds);

      return pushEntry(prev, {params: next, semanticOverrides: current.semanticOverrides});
    });
  };

  const loadPreset = (preset: PresetTheme) => {
    lastChangedKeyRef.current = null;
    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      id: _id,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      image: _image,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      label: _label,
      semanticOverrides: overrides,
      ...presetParams
    } = preset;

    setHistoryState((prev) =>
      pushEntry(prev, {params: presetParams, semanticOverrides: overrides}),
    );
  };

  const undo = () => {
    lastChangedKeyRef.current = null;
    setHistoryState((prev) => (prev.index > 0 ? {...prev, index: prev.index - 1} : prev));
  };

  const redo = () => {
    lastChangedKeyRef.current = null;
    setHistoryState((prev) =>
      prev.index < prev.entries.length - 1 ? {...prev, index: prev.index + 1} : prev,
    );
  };

  // Keyboard shortcuts: Ctrl/Cmd+Z → undo, Ctrl/Cmd+Y or Ctrl/Cmd+Shift+Z → redo
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const isMod = e.ctrlKey || e.metaKey;

      if (!isMod) return;

      if (e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if (e.key === "y" || (e.key === "z" && e.shiftKey)) {
        e.preventDefault();
        redo();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, undo, redo]);

  return (
    <ThemeBuilderContext
      value={{
        canRedo,
        canUndo,
        isCodePanelOpen,
        isOpen,
        loadPreset,
        lockedKeys,
        params,
        randomize,
        redo,
        resetParams,
        semanticOverrides,
        setParam,
        setParams,
        toggleCodePanel,
        toggleLock,
        toggleOpen,
        undo,
      }}
    >
      {children}
    </ThemeBuilderContext>
  );
}

/* -------------------------------------------------------------------------------------------------
 * Hook
 * -----------------------------------------------------------------------------------------------*/

export function useThemeBuilder(): ThemeBuilderContextValue {
  const ctx = use(ThemeBuilderContext);

  if (!ctx) throw new Error("useThemeBuilder must be used within a ThemeBuilderProvider");

  return ctx;
}
